import React, { useState, useEffect, useRef, useCallback } from 'react';
import api from '../../../services/api';
import { useAuth } from '../../../context/AuthContext';
import { useLocation } from 'react-router-dom';

// Resolve the backend host once. The chat upload service stores files under
// /uploads/<tenantId>/chat/... and serves them through the auth-gated route
// `serveAuthenticatedUpload` — so `<img src>` and `<a href>` need both:
//   1. the correct origin (production host, not hardcoded localhost)
//   2. a `?t=<jwt>` query param because <img> tags can't send headers.
const ASSET_HOST = (process.env.REACT_APP_API_URL || 'http://localhost:5000/api')
  .replace(/\/api\/?$/, '');
const withAssetAuth = (relUrl) => {
  if (!relUrl) return '';
  // Already absolute / data URI → leave alone.
  if (/^(https?:|data:)/.test(relUrl)) return relUrl;
  const token = localStorage.getItem('token');
  const url = `${ASSET_HOST}${relUrl}`;
  if (!token) return url;
  return url.includes('?')
    ? `${url}&t=${encodeURIComponent(token)}`
    : `${url}?t=${encodeURIComponent(token)}`;
};

// ─── BSModal ─────────────────────────────────────────────────────────────────
const BSModal = ({ show, onClose, title, size = '', children, footer }) =>
  show ? (
    <div
      className="modal fade show d-block"
      style={{ background: 'rgba(0,0,0,0.45)', zIndex: 1050 }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className={`modal-dialog modal-dialog-centered modal-dialog-scrollable ${size}`}>
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">{title}</h5>
            <button className="btn-close" onClick={onClose} />
          </div>
          <div className="modal-body">{children}</div>
          {footer && <div className="modal-footer">{footer}</div>}
        </div>
      </div>
    </div>
  ) : null;

// ─── NewGroupDialog ───────────────────────────────────────────────────────────
const NewGroupDialog = ({ show, onClose, users, onCreated, setAlert }) => {
  const [name, setName]               = useState('');
  const [desc, setDesc]               = useState('');
  const [selected, setSelected]       = useState([]);
  const [saving, setSaving]           = useState(false);

  const toggle = (uid) => {
    setSelected((prev) =>
      prev.includes(uid) ? prev.filter((x) => x !== uid) : [...prev, uid]
    );
  };

  const handleCreate = async () => {
    if (!name.trim()) { setAlert({ show: true, msg: 'Room name is required', type: 'danger' }); return; }
    setSaving(true);
    try {
      const r = await api.post('/training/chat/rooms/group', {
        name: name.trim(),
        description: desc.trim(),
        memberIds: selected,
      });
      onCreated(r.data?.room || r.data?.data?.room);
      setAlert({ show: true, msg: 'Group created', type: 'success' });
      onClose();
      setName(''); setDesc(''); setSelected([]);
    } catch (e) {
      setAlert({ show: true, msg: e?.response?.data?.message || 'Failed to create group', type: 'danger' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <BSModal
      show={show}
      onClose={onClose}
      title="New Group"
      size="modal-lg"
      footer={
        <>
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleCreate} disabled={saving}>
            {saving ? 'Creating…' : 'Create Group'}
          </button>
        </>
      }
    >
      <div className="mb-3">
        <label className="form-label fw-semibold">Group Name <span className="text-danger">*</span></label>
        <input className="form-control" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Project Team" />
      </div>
      <div className="mb-3">
        <label className="form-label fw-semibold">Description</label>
        <input className="form-control" value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="Optional description" />
      </div>
      <div className="mb-2">
        <label className="form-label fw-semibold">Add Members</label>
        <div style={{ maxHeight: 260, overflowY: 'auto', border: '1px solid #dee2e6', borderRadius: 6 }}>
          {users.length === 0 && <div className="text-muted p-3">No users available</div>}
          {users.map((u) => (
            <div
              key={u._id}
              className={`d-flex align-items-center gap-2 px-3 py-2 border-bottom ${selected.includes(u._id) ? 'bg-primary bg-opacity-10' : ''}`}
              style={{ cursor: 'pointer' }}
              onClick={() => toggle(u._id)}
            >
              <input type="checkbox" className="form-check-input m-0" checked={selected.includes(u._id)} readOnly />
              <div>
                <div className="fw-semibold small">{u.firstName} {u.lastName}</div>
                <div className="text-muted" style={{ fontSize: 11 }}>{u.roleKey}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </BSModal>
  );
};

// ─── DMDialog ────────────────────────────────────────────────────────────────
const DMDialog = ({ show, onClose, users, onCreated, setAlert }) => {
  const [search, setSearch]   = useState('');
  const [saving, setSaving]   = useState(false);

  const filtered = users.filter(
    (u) =>
      `${u.firstName} ${u.lastName}`.toLowerCase().includes(search.toLowerCase()) ||
      (u.roleKey || '').toLowerCase().includes(search.toLowerCase())
  );

  const handleSelect = async (u) => {
    setSaving(true);
    try {
      const r = await api.post('/training/chat/rooms/direct', { targetUserId: u._id });
      onCreated(r.data?.room || r.data?.data?.room);
      onClose();
      setSearch('');
    } catch (e) {
      setAlert({ show: true, msg: e?.response?.data?.message || 'Failed to start DM', type: 'danger' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <BSModal show={show} onClose={onClose} title="Direct Message">
      <input
        className="form-control mb-3"
        placeholder="Search users…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        autoFocus
      />
      <div style={{ maxHeight: 320, overflowY: 'auto' }}>
        {filtered.length === 0 && <div className="text-muted text-center py-3">No users found</div>}
        {filtered.map((u) => (
          <div
            key={u._id}
            className="d-flex align-items-center gap-3 px-3 py-2 border-bottom"
            style={{ cursor: saving ? 'not-allowed' : 'pointer' }}
            onClick={() => !saving && handleSelect(u)}
          >
            <div
              className="rounded-circle bg-secondary text-white d-flex align-items-center justify-content-center flex-shrink-0"
              style={{ width: 36, height: 36, fontSize: 14, fontWeight: 600 }}
            >
              {(u.firstName?.[0] || '?').toUpperCase()}
            </div>
            <div>
              <div className="fw-semibold">{u.firstName} {u.lastName}</div>
              <div className="text-muted small">{u.roleKey}</div>
            </div>
          </div>
        ))}
      </div>
    </BSModal>
  );
};

// ─── MembersDialog ────────────────────────────────────────────────────────────
const MembersDialog = ({ show, onClose, room, currentUserId, canAdminUser, onUpdated, setAlert }) => {
  const [saving, setSaving] = useState(false);

  if (!room) return null;

  const handleRemove = async (memberId) => {
    if (!window.confirm('Remove this member?')) return;
    setSaving(true);
    try {
      await api.delete(`/training/chat/rooms/${room._id}/members/${memberId}`);
      setAlert({ show: true, msg: 'Member removed', type: 'success' });
      onUpdated();
    } catch (e) {
      setAlert({ show: true, msg: e?.response?.data?.message || 'Failed to remove', type: 'danger' });
    } finally {
      setSaving(false);
    }
  };

  const handlePermChange = async (memberId, permission) => {
    setSaving(true);
    try {
      await api.patch(`/training/chat/rooms/${room._id}`, {
        memberPermissions: [{ userId: memberId, permission }],
      });
      setAlert({ show: true, msg: 'Permission updated', type: 'success' });
      onUpdated();
    } catch (e) {
      setAlert({ show: true, msg: e?.response?.data?.message || 'Failed to update', type: 'danger' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <BSModal show={show} onClose={onClose} title={`Members — ${room.name}`} size="modal-lg">
      <div style={{ maxHeight: 400, overflowY: 'auto' }}>
        {(room.members || []).map((m) => {
          const u = m.user || {};
          const uid = u._id || m.user;
          const name = `${u.firstName || ''} ${u.lastName || ''}`.trim() || 'Unknown';
          const isSelf = uid?.toString() === currentUserId?.toString();
          return (
            <div key={uid} className="d-flex align-items-center gap-3 px-2 py-2 border-bottom">
              <div
                className="rounded-circle bg-secondary text-white d-flex align-items-center justify-content-center flex-shrink-0"
                style={{ width: 36, height: 36, fontSize: 13, fontWeight: 600 }}
              >
                {(u.firstName?.[0] || '?').toUpperCase()}
              </div>
              <div className="flex-grow-1">
                <div className="fw-semibold small">{name} {isSelf && <span className="text-muted">(you)</span>}</div>
                <div className="text-muted" style={{ fontSize: 11 }}>{u.roleKey || ''} · {m.role}</div>
              </div>
              {canAdminUser && !isSelf && (
                <>
                  <select
                    className="form-select form-select-sm"
                    style={{ width: 130 }}
                    value={m.permission}
                    onChange={(e) => handlePermChange(uid, e.target.value)}
                    disabled={saving}
                  >
                    <option value="send">Send</option>
                    <option value="read_only">Read Only</option>
                    <option value="no_access">No Access</option>
                  </select>
                  {!room.isDefault && (
                    <button
                      className="btn btn-sm btn-outline-danger"
                      onClick={() => handleRemove(uid)}
                      disabled={saving}
                    >
                      Remove
                    </button>
                  )}
                </>
              )}
            </div>
          );
        })}
      </div>
    </BSModal>
  );
};

// ─── Utilities ────────────────────────────────────────────────────────────────
const formatTime = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toLocaleDateString();
};

const getInitial = (name) => (name ? name[0].toUpperCase() : '?');

// ─── ChatPage ─────────────────────────────────────────────────────────────────
export default function ChatPage() {
  const { user } = useAuth();
  const location = useLocation();
  const role     = user?.roleKey || user?.role || '';
  const myId     = user?._id || user?.id || '';
  const canAdmin = ['admin', 'manager', 'team_lead'].includes(role);

  const [rooms,            setRooms]            = useState([]);
  const [activeRoom,       setActiveRoom]        = useState(null);
  const [messages,         setMessages]          = useState([]);
  const [msgText,          setMsgText]           = useState('');
  const [replyTo,          setReplyTo]           = useState(null);
  const [loadingRooms,     setLoadingRooms]      = useState(true);
  const [loadingMsgs,      setLoadingMsgs]       = useState(false);
  const [roomSearch,       setRoomSearch]        = useState('');
  const [showNewGroup,     setShowNewGroup]      = useState(false);
  const [showDM,           setShowDM]            = useState(false);
  const [users,            setUsers]             = useState([]);
  const [alert,            setAlert]             = useState({ show: false, msg: '', type: 'success' });
  const [manageMembersRoom, setManageMembersRoom] = useState(null);
  const [hoveredMsg,       setHoveredMsg]        = useState(null);
  const [sendingFile,      setSendingFile]       = useState(false);

  const messagesEndRef = useRef(null);
  const pollRef        = useRef(null);
  const fileInputRef   = useRef(null);
  const activeRoomRef  = useRef(null);
  const openedRoomFromQueryRef = useRef('');
  const msgListRef     = useRef(null);
  const [isAtBottom, setIsAtBottom] = useState(true);
  const [hasNewMsgs, setHasNewMsgs] = useState(false);

  // Keep activeRoomRef in sync
  useEffect(() => { activeRoomRef.current = activeRoom; }, [activeRoom]);

  // ── Auto-dismiss alert ──
  useEffect(() => {
    if (alert.show) {
      const t = setTimeout(() => setAlert((a) => ({ ...a, show: false })), 3500);
      return () => clearTimeout(t);
    }
  }, [alert]);

  // ── Load rooms ──
  const loadRooms = useCallback(async () => {
    try {
      const r = await api.get('/training/chat/rooms');
      // success() helper wraps plain objects under `data`, so the real path
      // is `r.data.data.rooms`, not `r.data.rooms`. Keep both for safety.
      setRooms(r.data?.data?.rooms || r.data?.rooms || []);
    } catch (_) {}
    setLoadingRooms(false);
  }, []);

  useEffect(() => { loadRooms(); }, [loadRooms]);

  // ── Load users (for DM / group dialogs) ──
  const loadUsers = useCallback(async () => {
    try {
      const r = await api.get('/training/chat/users');
      const raw = r.data?.data?.users || r.data?.users || r.data?.data?.records || r.data?.records || [];
      setUsers(raw.filter((u) => (u._id || u.id)?.toString() !== myId?.toString()));
    } catch (_) {}
  }, [myId]);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  useEffect(() => {
    if (!rooms.length) return;
    const params = new URLSearchParams(location.search || '');
    const roomId = params.get('roomId');
    if (!roomId || openedRoomFromQueryRef.current === roomId) return;
    const targetRoom = rooms.find((room) => String(room._id) === String(roomId));
    if (!targetRoom) return;
    openedRoomFromQueryRef.current = roomId;
    selectRoom(targetRoom);
  }, [rooms, location.search]); // eslint-disable-line

  // ── Load messages ──
  const loadMessages = useCallback(async (roomId, silent = false) => {
    if (!roomId) return;
    if (!silent) setLoadingMsgs(true);
    try {
      const r = await api.get(`/training/chat/rooms/${roomId}/messages`);
      const msgs = r.data?.data?.messages || r.data?.messages || [];
      setMessages(msgs);
      // Mark as read
      api.patch(`/training/chat/rooms/${roomId}/messages/read`).catch(() => {});
    } catch (_) {}
    if (!silent) setLoadingMsgs(false);
  }, []);

  // Scroll to bottom after messages change — only if at bottom
  useEffect(() => {
    if (isAtBottom) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    } else {
      setHasNewMsgs(true);
    }
  }, [messages]);

  // Track scroll position
  const handleScroll = () => {
    const el = msgListRef.current;
    if (!el) return;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 50;
    setIsAtBottom(atBottom);
    if (atBottom) setHasNewMsgs(false);
  };

  // ── Polling ──
  useEffect(() => {
    if (pollRef.current) clearInterval(pollRef.current);
    if (!activeRoom) return;

    pollRef.current = setInterval(async () => {
      const rid = activeRoomRef.current?._id;
      if (!rid) return;
      try {
        const r = await api.get(`/training/chat/rooms/${rid}/messages`);
        const msgs = r.data?.data?.messages || r.data?.messages || [];
        setMessages((prev) => {
          if (prev.length !== msgs.length) {
            api.patch(`/training/chat/rooms/${rid}/messages/read`).catch(() => {});
            // Also refresh room list for unread counts
            loadRooms();
          }
          return msgs;
        });
      } catch (_) {}
    }, 3000);

    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [activeRoom, loadRooms]);

  // ── Select room ──
  const selectRoom = async (room) => {
    setActiveRoom(room);
    setMessages([]);
    setReplyTo(null);
    setHasNewMsgs(false);
    setIsAtBottom(true);
    await loadMessages(room._id);
    // Refresh rooms to update unread count
    loadRooms();
  };

  // ── Send message ──
  const sendMessage = async () => {
    if (!msgText.trim() || !activeRoom) return;
    const rid = activeRoom._id;
    try {
      await api.post(`/training/chat/rooms/${rid}/messages`, {
        text: msgText.trim(),
        replyTo: replyTo?._id || undefined,
      });
      setMsgText('');
      setReplyTo(null);
      await loadMessages(rid, true);
      loadRooms();
    } catch (e) {
      setAlert({ show: true, msg: e?.response?.data?.message || 'Failed to send', type: 'danger' });
    }
  };

  // ── Send file ──
  const sendFile = async (file) => {
    if (!file || !activeRoom) return;
    setSendingFile(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await api.post(`/training/chat/rooms/${activeRoom._id}/messages/upload`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      await loadMessages(activeRoom._id, true);
      loadRooms();
    } catch (e) {
      setAlert({ show: true, msg: e?.response?.data?.message || 'Upload failed', type: 'danger' });
    } finally {
      setSendingFile(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // ── Delete message ──
  const deleteMessage = async (msg) => {
    if (!window.confirm('Delete this message?')) return;
    try {
      await api.delete(`/training/chat/rooms/${activeRoom._id}/messages/${msg._id}`);
      setMessages((prev) =>
        prev.map((m) => (m._id === msg._id ? { ...m, isDeleted: true, text: '' } : m))
      );
    } catch (e) {
      setAlert({ show: true, msg: e?.response?.data?.message || 'Delete failed', type: 'danger' });
    }
  };

  // ── Delete room ──
  const deleteRoom = async (room) => {
    if (!window.confirm(`Delete room "${room.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/training/chat/rooms/${room._id}`);
      setRooms((prev) => prev.filter((r) => r._id !== room._id));
      if (activeRoom?._id === room._id) { setActiveRoom(null); setMessages([]); }
      setAlert({ show: true, msg: 'Room deleted', type: 'success' });
    } catch (e) {
      setAlert({ show: true, msg: e?.response?.data?.message || 'Delete failed', type: 'danger' });
    }
  };

  // ── Filtered rooms ──
  const filteredRooms = rooms.filter((r) =>
    (r.displayName || r.name || '').toLowerCase().includes(roomSearch.toLowerCase())
  );

  // ── Message grouping by date ──
  const groupedMessages = () => {
    const groups = [];
    let lastDate = null;
    for (const msg of messages) {
      const d = formatDate(msg.createdAt);
      if (d !== lastDate) {
        groups.push({ type: 'date', date: d });
        lastDate = d;
      }
      groups.push({ type: 'msg', msg });
    }
    return groups;
  };

  const isOwn = (msg) => {
    const sid = msg.sender?._id || msg.sender;
    return sid?.toString() === myId?.toString();
  };

  const senderName = (msg) => {
    const s = msg.sender;
    if (!s) return 'Unknown';
    return `${s.firstName || ''} ${s.lastName || ''}`.trim() || 'Unknown';
  };

  // ── Handle textarea key ──
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && e.ctrlKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <div className="container-fluid px-3 py-2">
      {/* Alert */}
      {alert.show && (
        <div
          className={`alert alert-${alert.type} alert-dismissible py-2 px-3 mb-2`}
          style={{ position: 'fixed', top: 16, right: 16, zIndex: 9999, minWidth: 260 }}
        >
          {alert.msg}
          <button className="btn-close" onClick={() => setAlert((a) => ({ ...a, show: false }))} />
        </div>
      )}

      <div className="d-flex gap-0 border rounded overflow-hidden" style={{ height: 'calc(100vh - 120px)' }}>
        {/* ── Left: Rooms List ── */}
        <div
          className="d-flex flex-column border-end bg-white"
          style={{ width: 280, minWidth: 230, maxWidth: 320 }}
        >
          {/* Header */}
          <div className="px-3 pt-3 pb-2 border-bottom">
            <div className="d-flex align-items-center justify-content-between mb-2">
              <h6 className="mb-0 fw-bold">Chats</h6>
              <div className="d-flex gap-1">
                <button
                  className="btn btn-sm btn-outline-secondary"
                  title="Direct Message"
                  onClick={() => setShowDM(true)}
                  style={{ fontSize: 13, padding: '2px 8px' }}
                >
                  DM
                </button>
                {canAdmin && (
                  <button
                    className="btn btn-sm btn-primary"
                    title="New Group"
                    onClick={() => setShowNewGroup(true)}
                    style={{ fontSize: 13, padding: '2px 8px' }}
                  >
                    + Group
                  </button>
                )}
              </div>
            </div>
            <input
              className="form-control form-control-sm"
              placeholder="Search rooms…"
              value={roomSearch}
              onChange={(e) => setRoomSearch(e.target.value)}
            />
          </div>

          {/* Room list */}
          <div className="flex-grow-1 overflow-y-auto">
            {loadingRooms && (
              <div className="text-center py-4 text-muted">
                <div className="spinner-border spinner-border-sm me-2" /> Loading…
              </div>
            )}
            {!loadingRooms && filteredRooms.length === 0 && (
              <div className="text-center py-4 text-muted small">No rooms found</div>
            )}
            {filteredRooms.map((room) => {
              const isActive = activeRoom?._id === room._id;
              return (
                <div
                  key={room._id}
                  className={`px-3 py-2 border-bottom d-flex align-items-start gap-2 ${isActive ? 'bg-primary bg-opacity-10' : 'bg-white'}`}
                  style={{ cursor: 'pointer' }}
                  onClick={() => selectRoom(room)}
                >
                  {/* Avatar */}
                  <div
                    className={`rounded-circle d-flex align-items-center justify-content-center flex-shrink-0 text-white fw-bold ${isActive ? 'bg-primary' : 'bg-secondary'}`}
                    style={{ width: 36, height: 36, fontSize: 14 }}
                  >
                    {room.type === 'direct' ? getInitial(room.displayName) : '#'}
                  </div>
                  <div className="flex-grow-1 overflow-hidden">
                    <div className="d-flex justify-content-between align-items-center">
                      <span className="fw-semibold small text-truncate" style={{ maxWidth: 140 }}>
                        {room.displayName || room.name}
                      </span>
                      {room.unreadCount > 0 && (
                        <span className="badge bg-danger rounded-pill" style={{ fontSize: 10 }}>
                          {room.unreadCount > 99 ? '99+' : room.unreadCount}
                        </span>
                      )}
                    </div>
                    {room.lastMessage?.text && (
                      <div className="text-muted text-truncate" style={{ fontSize: 11 }}>
                        {room.lastMessage.senderName
                          ? `${room.lastMessage.senderName}: `
                          : ''}
                        {room.lastMessage.text}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* ── Right: Message Panel ── */}
        {!activeRoom ? (
          <div className="flex-grow-1 d-flex align-items-center justify-content-center bg-light">
            <div className="text-center text-muted">
              <div style={{ fontSize: 48 }}>💬</div>
              <div className="mt-2">Select a room to start chatting</div>
            </div>
          </div>
        ) : (
          <div className="flex-grow-1 d-flex flex-column bg-white">
            {/* Room header */}
            <div className="px-3 py-2 border-bottom d-flex align-items-center justify-content-between bg-white">
              <div>
                <span className="fw-bold">
                  {activeRoom.type === 'direct' ? '' : '# '}
                  {activeRoom.displayName || activeRoom.name}
                </span>
                <span className="text-muted small ms-2">
                  {(activeRoom.members || []).length} members
                </span>
                {activeRoom.unreadCount > 0 && (
                  <span className="badge bg-danger ms-2" style={{ fontSize: 10 }}>
                    {activeRoom.unreadCount} unread
                  </span>
                )}
              </div>
              <div className="d-flex gap-2 align-items-center">
                {canAdmin && (
                  <button
                    className="btn btn-sm btn-outline-secondary"
                    onClick={() => setManageMembersRoom(activeRoom)}
                  >
                    Members
                  </button>
                )}
                {canAdmin && !activeRoom.isDefault && (
                  <button
                    className="btn btn-sm btn-outline-danger"
                    onClick={() => deleteRoom(activeRoom)}
                  >
                    Delete
                  </button>
                )}
                <button
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() => { loadMessages(activeRoom._id); loadRooms(); }}
                  title="Refresh"
                >
                  ↻
                </button>
              </div>
            </div>

            {/* Messages */}
            <div className="flex-grow-1 overflow-y-auto px-3 py-2 position-relative" style={{ overflowY: 'auto' }} ref={msgListRef} onScroll={handleScroll}>
              {loadingMsgs && (
                <div className="text-center py-3 text-muted">
                  <div className="spinner-border spinner-border-sm me-2" /> Loading messages…
                </div>
              )}

              {groupedMessages().map((item, idx) => {
                if (item.type === 'date') {
                  return (
                    <div key={`date-${idx}`} className="text-center my-3">
                      <span className="badge bg-light text-muted border px-3 py-1" style={{ fontSize: 11 }}>
                        {item.date}
                      </span>
                    </div>
                  );
                }

                const msg = item.msg;
                const own  = isOwn(msg);
                const name = senderName(msg);

                // System message
                if (msg.type === 'system') {
                  return (
                    <div key={msg._id} className="text-center my-2">
                      <em className="text-muted small">{msg.text}</em>
                    </div>
                  );
                }

                // Deleted
                if (msg.isDeleted) {
                  return (
                    <div key={msg._id} className={`d-flex mb-2 ${own ? 'justify-content-end' : 'justify-content-start'}`}>
                      <span className="text-muted fst-italic small px-3 py-2 border rounded">
                        [message deleted]
                      </span>
                    </div>
                  );
                }

                return (
                  <div
                    key={msg._id}
                    className={`d-flex mb-2 ${own ? 'justify-content-end' : 'justify-content-start'}`}
                    onMouseEnter={() => setHoveredMsg(msg._id)}
                    onMouseLeave={() => setHoveredMsg(null)}
                  >
                    {/* Avatar for others */}
                    {!own && (
                      <div
                        className="rounded-circle bg-secondary text-white d-flex align-items-center justify-content-center flex-shrink-0 me-2 align-self-end"
                        style={{ width: 30, height: 30, fontSize: 12, fontWeight: 600 }}
                      >
                        {getInitial(name)}
                      </div>
                    )}

                    <div style={{ maxWidth: '70%' }}>
                      {/* Sender name for group */}
                      {!own && activeRoom.type === 'group' && (
                        <div className="text-muted mb-1" style={{ fontSize: 11, paddingLeft: 4 }}>
                          {name}
                        </div>
                      )}

                      {/* Reply preview */}
                      {msg.replyTo && !msg.replyTo.isDeleted && (
                        <div
                          className="border-start border-3 border-secondary ps-2 mb-1 text-muted small"
                          style={{ fontSize: 11 }}
                        >
                          {msg.replyTo.text || `[${msg.replyTo.type || 'file'}]`}
                        </div>
                      )}

                      {/* Bubble */}
                      <div
                        className={`px-3 py-2 rounded-3 position-relative ${
                          own ? 'bg-primary text-white' : 'bg-light text-dark border'
                        }`}
                        style={{ wordBreak: 'break-word' }}
                      >
                        {/* Content */}
                        {msg.type === 'image' && msg.fileUrl ? (
                          <div>
                            <a href={withAssetAuth(msg.fileUrl)} target="_blank" rel="noreferrer">
                              <img
                                src={withAssetAuth(msg.fileUrl)}
                                alt={msg.fileName || 'image'}
                                style={{ maxHeight: 200, maxWidth: '100%', borderRadius: 6, display: 'block', cursor: 'zoom-in' }}
                              />
                            </a>
                            {msg.fileName && (
                              <div className="small mt-1 opacity-75">{msg.fileName}</div>
                            )}
                            <div className="d-flex gap-2 mt-1">
                              <a href={withAssetAuth(msg.fileUrl)} target="_blank" rel="noreferrer"
                                 className={`small ${own ? 'text-white' : 'text-primary'}`}
                                 style={{ textDecoration: 'underline' }}>
                                <i className="bi bi-eye me-1"/>View
                              </a>
                              <a href={withAssetAuth(msg.fileUrl)} download={msg.fileName || 'image'}
                                 className={`small ${own ? 'text-white' : 'text-primary'}`}
                                 style={{ textDecoration: 'underline' }}>
                                <i className="bi bi-download me-1"/>Download
                              </a>
                            </div>
                          </div>
                        ) : msg.type === 'file' && msg.fileUrl ? (
                          // Explicit file card with name, size, and View / Download
                          // buttons — the previous "📎 filename" link was easy to
                          // miss as clickable, so users thought file uploads had no
                          // way to be opened.
                          <div
                            className={`p-2 rounded ${own ? '' : 'border'}`}
                            style={{
                              background: own ? 'rgba(255,255,255,0.18)' : '#f8fafc',
                              minWidth: 220,
                            }}
                          >
                            <div className="d-flex align-items-center gap-2 mb-1">
                              <i className="bi bi-file-earmark-fill" style={{ fontSize: 22 }}/>
                              <div style={{ minWidth: 0, flex: 1 }}>
                                <div className="fw-semibold small text-truncate" title={msg.fileName}>
                                  {msg.fileName || 'Attachment'}
                                </div>
                                {msg.fileSize ? (
                                  <div className="small opacity-75" style={{ fontSize: 11 }}>
                                    {msg.fileSize >= 1024 * 1024
                                      ? `${(msg.fileSize / (1024*1024)).toFixed(1)} MB`
                                      : `${Math.max(1, Math.round(msg.fileSize / 1024))} KB`}
                                  </div>
                                ) : null}
                              </div>
                            </div>
                            <div className="d-flex gap-2">
                              <a
                                href={withAssetAuth(msg.fileUrl)}
                                target="_blank"
                                rel="noreferrer"
                                className={`btn btn-sm ${own ? 'btn-light' : 'btn-outline-primary'} flex-fill`}
                                style={{ fontSize: 11, padding: '2px 8px' }}
                              >
                                <i className="bi bi-eye me-1"/>View
                              </a>
                              <a
                                href={withAssetAuth(msg.fileUrl)}
                                download={msg.fileName || 'file'}
                                className={`btn btn-sm ${own ? 'btn-light' : 'btn-outline-primary'} flex-fill`}
                                style={{ fontSize: 11, padding: '2px 8px' }}
                              >
                                <i className="bi bi-download me-1"/>Download
                              </a>
                            </div>
                          </div>
                        ) : (
                          <span style={{ whiteSpace: 'pre-wrap' }}>{msg.text}</span>
                        )}

                        {/* Timestamp */}
                        <div
                          className={`mt-1 text-end ${own ? 'text-white opacity-75' : 'text-muted'}`}
                          style={{ fontSize: 10 }}
                        >
                          {formatTime(msg.createdAt)}
                        </div>
                      </div>

                      {/* Hover actions */}
                      {hoveredMsg === msg._id && (
                        <div
                          className={`d-flex gap-1 mt-1 ${own ? 'justify-content-end' : 'justify-content-start'}`}
                        >
                          <button
                            className="btn btn-sm btn-outline-secondary py-0 px-2"
                            style={{ fontSize: 11 }}
                            onClick={() => setReplyTo(msg)}
                          >
                            Reply
                          </button>
                          {(own || canAdmin) && (
                            <button
                              className="btn btn-sm btn-outline-danger py-0 px-2"
                              style={{ fontSize: 11 }}
                              onClick={() => deleteMessage(msg)}
                            >
                              Delete
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
              <div ref={messagesEndRef} />

              {/* New messages indicator */}
              {hasNewMsgs && !isAtBottom && (
                <div
                  className="position-absolute bottom-0 start-50 translate-middle-x mb-2"
                  style={{ zIndex: 5 }}
                >
                  <button
                    className="btn btn-primary btn-sm shadow rounded-pill px-3"
                    onClick={() => {
                      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
                      setHasNewMsgs(false);
                      setIsAtBottom(true);
                    }}
                  >
                    <i className="bi bi-arrow-down me-1"/>New messages ↓
                  </button>
                </div>
              )}
            </div>

            {/* Reply-to strip */}
            {replyTo && (
              <div className="px-3 py-2 border-top bg-light d-flex align-items-center gap-2">
                <div className="flex-grow-1 small text-muted">
                  <span className="fw-semibold">Replying to {senderName(replyTo)}: </span>
                  {replyTo.text || `[${replyTo.type}]`}
                </div>
                <button className="btn-close" style={{ fontSize: 10 }} onClick={() => setReplyTo(null)} />
              </div>
            )}

            {/* Message input */}
            <div className="px-3 py-2 border-top bg-white">
              {activeRoom.myPermission === 'read_only' || activeRoom.myPermission === 'no_access' ? (
                <div className="text-center text-muted small py-2">
                  You can only read messages in this room.
                </div>
              ) : (
                <div className="d-flex align-items-end gap-2">
                  {/* File input */}
                  <input
                    type="file"
                    ref={fileInputRef}
                    className="d-none"
                    onChange={(e) => sendFile(e.target.files[0])}
                  />
                  <button
                    className="btn btn-outline-secondary flex-shrink-0"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={sendingFile}
                    title="Attach file"
                  >
                    {sendingFile ? (
                      <span className="spinner-border spinner-border-sm" />
                    ) : (
                      '📎'
                    )}
                  </button>

                  <textarea
                    className="form-control"
                    rows={2}
                    placeholder="Type a message… (Ctrl+Enter to send)"
                    value={msgText}
                    onChange={(e) => setMsgText(e.target.value)}
                    onKeyDown={handleKeyDown}
                    style={{ resize: 'none' }}
                  />

                  <button
                    className="btn btn-primary flex-shrink-0"
                    onClick={sendMessage}
                    disabled={!msgText.trim()}
                  >
                    Send
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Dialogs */}
      <NewGroupDialog
        show={showNewGroup}
        onClose={() => setShowNewGroup(false)}
        users={users}
        onCreated={(room) => {
          if (room) {
            setRooms((prev) => [{ ...room, displayName: room.name, unreadCount: 0 }, ...prev]);
          }
          loadRooms();
        }}
        setAlert={setAlert}
      />

      <DMDialog
        show={showDM}
        onClose={() => setShowDM(false)}
        users={users}
        onCreated={(room) => {
          if (room) {
            loadRooms();
            selectRoom(room);
          }
        }}
        setAlert={setAlert}
      />

      <MembersDialog
        show={!!manageMembersRoom}
        onClose={() => setManageMembersRoom(null)}
        room={manageMembersRoom}
        currentUserId={myId}
        canAdminUser={canAdmin}
        onUpdated={() => {
          loadRooms();
          if (activeRoom) loadMessages(activeRoom._id, true);
        }}
        setAlert={setAlert}
      />
    </div>
  );
}
